import { useEffect, useState, useCallback } from 'react';
import adminApi from '@/utils/adminApi';
import { TrendingUp, Users, Calendar, BarChart3, Globe, MapPin, Ban, X, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ANALYTICS_REFRESH_MS = 30000;

export default function VisitorAnalytics() {
  const [stats, setStats] = useState(null);
  const [recentVisitors, setRecentVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRecentVisitors, setShowRecentVisitors] = useState(false);
  const [blockedIPs, setBlockedIPs] = useState(new Set());
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateVisitors, setDateVisitors] = useState([]);
  const [loadingDateVisitors, setLoadingDateVisitors] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminApi.get(`/analytics/visitors`);
      setStats(response.data);
    } catch (error) {
      // Non-critical analytics failure
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch analytics:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRecentVisitors = useCallback(async () => {
    try {
      const response = await adminApi.get(`/analytics/recent-visitors`);
      setRecentVisitors(response.data);
    } catch (error) {
      // Non-critical analytics failure
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch recent visitors:', error);
      }
    }
  }, []);

  const fetchBlockedIPs = useCallback(async () => {
    try {
      const response = await adminApi.get(`/blocked-ips`);
      setBlockedIPs(new Set(response.data.map(item => item.ip_address)));
    } catch (error) {
      // Non-critical - blocked IPs list won't affect main functionality
      if (process.env.NODE_ENV === 'development') {
        console.debug('Failed to fetch blocked IPs:', error.message);
      }
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchRecentVisitors();
    fetchBlockedIPs();
    
    const interval = setInterval(() => {
      fetchStats();
      fetchRecentVisitors();
    }, ANALYTICS_REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchStats, fetchRecentVisitors, fetchBlockedIPs]);

  const handleBlockIP = async (ip) => {
    if (!ip || ip === 'Unknown') return;
    
    if (!window.confirm(`Block IP address ${ip}?`)) return;
    
    try {
      await adminApi.post(`/blocked-ips`, {
        ip_address: ip,
        reason: 'Blocked from visitor analytics'
      });
      toast.success(`IP ${ip} has been blocked`);
      setBlockedIPs(prev => new Set([...prev, ip]));
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to block IP');
      }
    }
  };

  const handleDateClick = async (date) => {
    if (selectedDate === date) {
      // Toggle off if clicking the same date
      setSelectedDate(null);
      setDateVisitors([]);
      return;
    }
    
    setSelectedDate(date);
    setLoadingDateVisitors(true);
    
    try {
      const response = await adminApi.get(`/analytics/visitors-by-date?date=${date}`);
      setDateVisitors(response.data);
    } catch (error) {
      toast.error('Failed to load visitors for this date');
      setDateVisitors([]);
    } finally {
      setLoadingDateVisitors(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-2xl border border-stone-100">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-stone-200 rounded w-1/3"></div>
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-stone-100 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const statCards = [
    {
      label: 'Total Visitors',
      value: stats.total_visitors,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      label: 'Today',
      value: stats.today_visitors,
      icon: Calendar,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      label: 'This Week',
      value: stats.this_week_visitors,
      icon: TrendingUp,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      label: 'This Month',
      value: stats.this_month_visitors,
      icon: BarChart3,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
  ];

  // Get daily breakdown sorted by date
  const dailyData = Object.entries(stats.daily_breakdown || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-7); // Last 7 days

  const maxValue = Math.max(...dailyData.map(([, value]) => value), 1);

  return (
    <div className="space-y-6 mb-8">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const IconComponent = stat.icon;
          return (
            <div
              key={stat.label}
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
              className="bg-white p-6 rounded-2xl border border-stone-100 hover:border-[#E85D75]/30 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                  <IconComponent className={`w-6 h-6 ${stat.color}`} />
                </div>
              </div>
              <p className="text-3xl font-serif font-semibold text-stone-800 mb-1">
                {stat.value.toLocaleString()}
              </p>
              <p className="text-sm text-stone-600">{stat.label}</p>
            </div>
          );
        })}
      </div>

      {/* Daily Breakdown Chart */}
      <div className="bg-white p-6 rounded-2xl border border-stone-100">
        <h3 className="text-lg font-serif font-medium text-stone-800 mb-2">
          Last 7 Days Activity
        </h3>
        <p className="text-sm text-stone-500 mb-6">Click on a day to see visitor details</p>
        <div className="space-y-4">
          {dailyData.map(([date, count]) => {
            const percentage = (count / maxValue) * 100;
            const dateObj = new Date(date);
            const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
            const dateStr = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const isSelected = selectedDate === date;

            return (
              <div key={date}>
                <button
                  onClick={() => handleDateClick(date)}
                  className={`w-full text-left p-3 rounded-xl transition-all ${
                    isSelected 
                      ? 'bg-[#E85D75]/10 border-2 border-[#E85D75]' 
                      : 'hover:bg-stone-50 border-2 border-transparent'
                  }`}
                >
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-stone-600 flex items-center gap-2">
                      {dayName}, {dateStr}
                      <ChevronRight className={`w-4 h-4 transition-transform ${isSelected ? 'rotate-90' : ''}`} />
                    </span>
                    <span className="font-semibold text-stone-800">{count} visitors</span>
                  </div>
                  <div className="w-full bg-stone-100 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-[#E85D75] to-[#D64560] h-full rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </button>
                
                {/* Expandable visitor list for selected date */}
                {isSelected && (
                  <div className="mt-2 ml-4 p-4 bg-stone-50 rounded-xl border border-stone-200">
                    {loadingDateVisitors ? (
                      <p className="text-stone-500 text-sm">Loading visitors...</p>
                    ) : dateVisitors.length === 0 ? (
                      <p className="text-stone-500 text-sm">No visitor data for this date</p>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        <p className="text-xs text-stone-500 mb-2">{dateVisitors.length} visitors on this day</p>
                        {dateVisitors.map((visitor, idx) => (
                          <div 
                            key={visitor.id || idx} 
                            className="flex items-center justify-between text-sm py-2 border-b border-stone-200 last:border-0"
                          >
                            <div className="flex items-center gap-2">
                              <MapPin className="w-3 h-3 text-stone-400" />
                              <span className={`font-mono ${blockedIPs.has(visitor.ip_address) ? 'text-red-600 line-through' : 'text-stone-700'}`}>
                                {visitor.ip_address || 'Unknown'}
                              </span>
                              {blockedIPs.has(visitor.ip_address) && (
                                <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">Blocked</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-stone-500 text-xs">{visitor.page}</span>
                              <span className="text-stone-400 text-xs">
                                {new Date(visitor.timestamp).toLocaleTimeString()}
                              </span>
                              {visitor.ip_address && visitor.ip_address !== 'Unknown' && !blockedIPs.has(visitor.ip_address) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBlockIP(visitor.ip_address);
                                  }}
                                  className="text-red-500 hover:text-red-700 p-1"
                                  title="Block IP"
                                >
                                  <Ban className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {dailyData.length === 0 && (
          <div className="text-center py-8 text-stone-500">
            <BarChart3 className="w-12 h-12 mx-auto mb-2 text-stone-300" />
            <p>No visitor data yet</p>
          </div>
        )}
      </div>

      {/* Recent Visitors Table */}
      <div className="bg-white p-6 rounded-2xl border border-stone-100">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#E85D75]" />
            <h3 className="text-lg font-serif font-medium text-stone-800">
              Recent Visitors
            </h3>
          </div>
          <button
            onClick={() => setShowRecentVisitors(!showRecentVisitors)}
            className="text-sm text-[#E85D75] hover:text-[#D64560] font-medium"
          >
            {showRecentVisitors ? 'Hide' : 'Show'} Details
          </button>
        </div>

        {showRecentVisitors && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="text-left py-3 px-4 font-semibold text-stone-600">IP Address</th>
                  <th className="text-left py-3 px-4 font-semibold text-stone-600">Page Visited</th>
                  <th className="text-left py-3 px-4 font-semibold text-stone-600">Time</th>
                  <th className="text-left py-3 px-4 font-semibold text-stone-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {recentVisitors.length > 0 ? (
                  recentVisitors.map((visitor, index) => (
                    <tr key={visitor.id || index} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-stone-400" />
                          <span className={`font-mono ${blockedIPs.has(visitor.ip_address) ? 'text-red-600 line-through' : 'text-stone-800'}`}>
                            {visitor.ip_address || 'Unknown'}
                          </span>
                          {blockedIPs.has(visitor.ip_address) && (
                            <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Blocked</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-stone-600">{visitor.page}</td>
                      <td className="py-3 px-4 text-stone-500">
                        {new Date(visitor.timestamp).toLocaleString()}
                      </td>
                      <td className="py-3 px-4">
                        {visitor.ip_address && visitor.ip_address !== 'Unknown' && !blockedIPs.has(visitor.ip_address) && (
                          <button
                            onClick={() => handleBlockIP(visitor.ip_address)}
                            className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1 rounded"
                          >
                            <Ban className="w-3 h-3" />
                            Block
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="py-8 text-center text-stone-500">
                      No visitor data yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!showRecentVisitors && recentVisitors.length > 0 && (
          <p className="text-sm text-stone-500">
            {recentVisitors.length} recent visitors recorded. Click "Show Details" to view IP addresses.
          </p>
        )}
      </div>
    </div>
  );
}
